import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { MatrixCriterion, MatrixCriterionValue } from '../../../types/advise';
import {
  fetchAdviseFunctionalityMatrix,
  updateAdviseMatrixCriterion,
  updateAdviseMatrixFeature
} from '../../../Api/Advise/Engagements';
import { addError } from '../../../store/Error';
import { EngagementFunctionalityMatrix } from '../../Compounds/Advise/SideBySideView';
import { usePendingSaves } from './usePendingSaves';

type PendingSaves = ReturnType<typeof usePendingSaves>;

const withCriterion = (criteria: MatrixCriterion[], id: string, value: MatrixCriterionValue) =>
  criteria.map((criterion) =>
    criterion.id === id
      ? { ...criterion, selected: value.selected, weight: value.selected ? value.weight : null }
      : criterion
  );

const withFeature = (criteria: MatrixCriterion[], id: string, selected: boolean) =>
  criteria.map((criterion) => ({
    ...criterion,
    categories: criterion.categories.map((category) => ({
      ...category,
      features: category.features.map((feature) => (feature.id === id ? { ...feature, selected } : feature))
    }))
  }));

/** The engagement's Functionality Matrix, loaded when its tab asks, with each change saved in the background. */
export const useEngagementFunctionalityMatrix = (
  engagementId: string | null,
  canEdit: boolean,
  { debouncedSave, flushSaves }: PendingSaves
): EngagementFunctionalityMatrix => {
  const [criteria, setCriteria] = useState<MatrixCriterion[] | null>(null);
  const [failed, setFailed] = useState(false);
  // A load that started before the user's latest change would put back what that change replaced.
  const edits = useRef(0);
  const loadedFor = useRef(engagementId);
  const shown = useRef(false);

  useEffect(() => {
    loadedFor.current = engagementId;
    shown.current = false;
    setCriteria(null);
    setFailed(false);
  }, [engagementId]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!engagementId) return;
    setFailed(false);
    // Counted before the flush: an edit made while it waits is in no save yet, so a load must not replace it.
    const editsBefore = edits.current;
    // A failed save stays queued for retry, so the server's older value must not replace what the user sees. With
    // nothing shown yet there is no matrix edit to protect, so a failing save elsewhere doesn't keep the tab empty.
    if (!(await flushSaves()) && shown.current) {
      if (loadedFor.current === engagementId) setFailed(true);
      return;
    }
    const { data, error } = await fetchAdviseFunctionalityMatrix(engagementId);
    if (loadedFor.current !== engagementId) return;
    // An edit made meanwhile is newer than this answer, so load again once that edit has saved.
    if (edits.current !== editsBefore) return refresh();
    if (error || !data) {
      addError('advise-engagement', error?.message);
      setFailed(true);
    } else {
      shown.current = true;
      setCriteria(data.criteria);
    }
  }, [engagementId, flushSaves]);

  // A feature Discovery has since removed answers 404 for good, so its save is dropped and the matrix reloaded.
  const saveOrReload = useCallback(
    (save: () => Promise<{ error: { message: string; errorResponse?: AxiosError } | null }>) => async () => {
      const result = await save();
      if (result.error?.errorResponse?.response?.status !== 404) return result;
      refresh();
      return { error: null };
    },
    [refresh]
  );

  const updateCriterion = useCallback(
    (criterionId: string, value: MatrixCriterionValue) => {
      if (!canEdit || !engagementId) return;
      edits.current += 1;
      setCriteria((prev) => prev && withCriterion(prev, criterionId, value));
      debouncedSave(
        `matrix:criterion:${criterionId}`,
        saveOrReload(() => updateAdviseMatrixCriterion(engagementId, criterionId, value))
      );
    },
    [canEdit, engagementId, debouncedSave, saveOrReload]
  );

  const selectFeature = useCallback(
    (featureId: string, selected: boolean) => {
      if (!canEdit || !engagementId) return;
      edits.current += 1;
      setCriteria((prev) => prev && withFeature(prev, featureId, selected));
      debouncedSave(
        `matrix:feature:${featureId}`,
        saveOrReload(() => updateAdviseMatrixFeature(engagementId, featureId, selected))
      );
    },
    [canEdit, engagementId, debouncedSave, saveOrReload]
  );

  return useMemo(
    () => ({ criteria, failed, refresh, updateCriterion, selectFeature }),
    [criteria, failed, refresh, updateCriterion, selectFeature]
  );
};
